import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { emptyFilters } from "../../constants/filterDefaults";
import { useDebounce } from "../../hooks/useDebounce";
import { countActiveFilters } from "../../utils/countActiveFilters";
import "./FilterPanel.css";

const cannabinoidsOptions = [
    { value: "CBDA", label: "CBDa" },
    { value: "CBGa", label: "CBGa" },
    { value: "CBCa", label: "CBCa" },
    { value: "THCa", label: "THCa" },
    { value: "THCVa", label: "THCVa" },
    { value: "THC", label: "THC" },
    { value: "CBD", label: "CBD" },
    { value: "CBN", label: "CBN" },
    { value: "CBDV", label: "CBDV" },
    { value: "CBG", label: "CBG" },
    { value: "CBC", label: "CBC" },
    { value: "THCV", label: "THCV" }, 
];

const terpeneOptions = [
    { value: "Limonene", label: "Limonene" },
    { value: "Myrcene", label: "Myrcene" },
    { value: "Linalool", label: "Linalool" },
    { value: "Terpinolene", label: "Terpinolene" },
    { value: "alpha_pinene", label: "Alpha Pinene" },
    { value: "alpha_humulene", label: "Alpha Humulene" },
    { value: "beta_caryophyllene", label: "Beta Caryophyllene" },
    { value: "beta_pinene", label: "Beta Pinene" },
];

const colorOptions = [
    { value: "yellow", label: "Yellow" },
    { value: "green", label: "Green" },
    { value: "blue", label: "Blue" },
    { value: "purple", label: "Purple" },
    { value: "navy", label: "Navy" },
];

const productTypeOptions = [
    { value: "flower", label: "Flower" },
    { value: "edible", label: "Edible" },
    { value: "concentrate", label: "Concentrate" },
    { value: "vape", label: "Vape" },
    { value: "preroll", label: "Preroll" },
];

const cannabinoidTypeOptions = [
    { value: "type I", label: "Type I (High THC)" },
    { value: "type II", label: "Type II (1:1 CBD:THC)" },
    { value: "type III", label: "Type III (High CBD)" },
];

// ── Sub-components ──────────────────────────────────────────────────────────

const FilterChip = ({ label, isSelected, onClick }) => (
    <button
        className={`fp-chip${isSelected ? " fp-chip--selected" : ""}`}
        onClick={onClick}
        type="button"
    >
        {label}
    </button>
);

const AccordionSection = ({ title, isOpen, onToggle, activeCount, children }) => (
    <div className="sfp-accordion">
        <button
            className="sfp-accordion__header"
            onClick={onToggle}
            type="button"
            aria-expanded={isOpen}
        >
            <span className="sfp-accordion__title">{title}</span>
            {!isOpen && activeCount > 0 && (
                <span className="sfp-accordion__badge">{activeCount}</span>
            )}
            <svg
                className={`sfp-accordion__chevron${isOpen ? " sfp-accordion__chevron--open" : ""}`}
                width="12" height="12" viewBox="0 0 12 12"
                fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round"
            >
                <polyline points="2,4 6,8 10,4" />
            </svg>
        </button>
        <div className={`sfp-accordion__body${isOpen ? " sfp-accordion__body--open" : ""}`}>
            <div className="sfp-accordion__content">
                {children}
            </div>
        </div>
    </div>
);

// ── Tag builder ─────────────────────────────────────────────────────────────

function buildTagList(filters) {
    const tags = [];

    if (filters.filterByProductName) {
        tags.push({ key: "search", label: `"${filters.filterByProductName}"`, field: "filterByProductName" });
    }

    (filters.selectedProductTypes || []).forEach((v) => {
        const opt = productTypeOptions.find((o) => o.value === v);
        tags.push({ key: `pt-${v}`, label: opt?.label || v, field: "selectedProductTypes", value: v });
    });

    (filters.selectedColors || []).forEach((v) => {
        const opt = colorOptions.find((o) => o.value === v);
        tags.push({ key: `color-${v}`, label: opt?.label || v, field: "selectedColors", value: v });
    });

    (filters.selectedTypes || []).forEach((v) => {
        const opt = cannabinoidTypeOptions.find((o) => o.value === v || `${o.value}v` === v);
        if (opt && !tags.some((t) => t.key === `type-${opt.value}`)) {
            tags.push({ key: `type-${opt.value}`, label: opt.label, field: "selectedTypes", value: opt.value });
        }
    });

    if (filters.price_min || filters.price_max) {
        const min = filters.price_min;
        const max = filters.price_max;
        let label = "Price: ";
        if (min && max) label += `$${min}–$${max}`;
        else if (min) label += `$${min}+`;
        else label += `≤$${max}`;
        tags.push({ key: "price", label, field: "price_range" });
    }

    if (filters.cannabinoid_level_min || filters.cannabinoid_level_max) {
        const min = filters.cannabinoid_level_min;
        const max = filters.cannabinoid_level_max;
        let label = "Cannabinoids: ";
        if (min && max) label += `${min}–${max}%`;
        else if (min) label += `${min}%+`;
        else label += `≤${max}%`;
        tags.push({ key: "cannabinoid_level", label, field: "cannabinoid_range" });
    }

    if (filters.terpene_level_min || filters.terpene_level_max) {
        const min = filters.terpene_level_min;
        const max = filters.terpene_level_max;
        let label = "Terpenes: ";
        if (min && max) label += `${min}–${max}%`;
        else if (min) label += `${min}%+`;
        else label += `≤${max}%`;
        tags.push({ key: "terpene_level", label, field: "terpene_range" });
    }

    (filters.filteredCannabinoids || []).forEach((v) => {
        const opt = cannabinoidsOptions.find((o) => o.value === v);
        tags.push({ key: `cann-${v}`, label: opt?.label || v, field: "filteredCannabinoids", value: v });
    });

    (filters.filteredTerpenes || []).forEach((v) => {
        const opt = terpeneOptions.find((o) => o.value === v);
        tags.push({ key: `terp-${v}`, label: opt?.label || v, field: "filteredTerpenes", value: v });
    });

    return tags;
}

// ── Main Component ──────────────────────────────────────────────────────────

const DEFAULT_OPEN = new Set(["productType", "color", "cannabinoidType"]);

const FilterPanel = ({
    isOpen,
    filters,
    onFilterChange,
    productsOrOperators,
    setProductsOrOperators,
}) => {
    const [openSections, setOpenSections] = useState(() => new Set(DEFAULT_OPEN));

    // Local state for debounced inputs
    const [localSearch, setLocalSearch] = useState(filters.filterByProductName || "");
    const [localRanges, setLocalRanges] = useState({
        price_min: filters.price_min || "",
        price_max: filters.price_max || "",
        cannabinoid_level_min: filters.cannabinoid_level_min || "",
        cannabinoid_level_max: filters.cannabinoid_level_max || "",
        terpene_level_min: filters.terpene_level_min || "",
        terpene_level_max: filters.terpene_level_max || "",
    });

    // Sync local state when filters change externally (e.g., tag removal, clear all)
    const filtersRef = useRef(filters);
    useEffect(() => {
        filtersRef.current = filters;
        setLocalSearch(filters.filterByProductName || "");
        setLocalRanges({
            price_min: filters.price_min || "",
            price_max: filters.price_max || "",
            cannabinoid_level_min: filters.cannabinoid_level_min || "",
            cannabinoid_level_max: filters.cannabinoid_level_max || "",
            terpene_level_min: filters.terpene_level_min || "",
            terpene_level_max: filters.terpene_level_max || "",
        });
    }, [filters]);

    // Debounce for text/range inputs
    const [emitDebounced] = useDebounce((updatedFilters) => {
        onFilterChange(updatedFilters);
    }, 300);

    // ── Handlers ──

    const toggleSection = (section) => {
        setOpenSections((prev) => {
            const next = new Set(prev);
            if (next.has(section)) next.delete(section);
            else next.add(section);
            return next;
        });
    };

    const toggleChip = useCallback((key, value) => {
        const arr = filtersRef.current[key] || [];
        const updated = arr.includes(value)
            ? arr.filter((v) => v !== value)
            : [...arr, value];
        onFilterChange({ ...filtersRef.current, [key]: updated });
    }, [onFilterChange]);

    const toggleType = useCallback((typeValue) => {
        const variations = [
            typeValue,
            `${typeValue}v`,
            `${typeValue}n`,
            `${typeValue}g`,
            `${typeValue}c`,
        ];
        const arr = filtersRef.current.selectedTypes || [];
        const allPresent = variations.every((v) => arr.includes(v));
        const updated = allPresent
            ? arr.filter((v) => !variations.includes(v))
            : [...new Set([...arr, ...variations])];
        onFilterChange({ ...filtersRef.current, selectedTypes: updated });
    }, [onFilterChange]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        setLocalSearch(val);
        emitDebounced({ ...filtersRef.current, filterByProductName: val || null });
    };

    const handleRangeChange = (key, val) => {
        setLocalRanges((prev) => ({ ...prev, [key]: val }));
        emitDebounced({ ...filtersRef.current, [key]: val || null });
    };

    // ── Tag removal ──

    const handleRemoveTag = useCallback((tag) => {
        const f = { ...filtersRef.current };
        switch (tag.field) {
            case "filterByProductName":
                f.filterByProductName = null;
                break;
            case "selectedProductTypes":
            case "selectedColors":
            case "filteredCannabinoids":
            case "filteredTerpenes":
                f[tag.field] = (f[tag.field] || []).filter((v) => v !== tag.value);
                break;
            case "selectedTypes": {
                const variations = [tag.value, `${tag.value}v`, `${tag.value}n`, `${tag.value}g`, `${tag.value}c`];
                f.selectedTypes = (f.selectedTypes || []).filter((v) => !variations.includes(v));
                break;
            }
            case "price_range":
                f.price_min = null;
                f.price_max = null;
                break;
            case "cannabinoid_range":
                f.cannabinoid_level_min = null;
                f.cannabinoid_level_max = null;
                break;
            case "terpene_range":
                f.terpene_level_min = null;
                f.terpene_level_max = null;
                break;
            default:
                break;
        }
        onFilterChange(f);
    }, [onFilterChange]);

    const handleClearAll = useCallback(() => {
        onFilterChange({ ...emptyFilters });
    }, [onFilterChange]);

    // ── Computed ──

    const tags = useMemo(() => buildTagList(filters), [filters]);

    const sectionCounts = useMemo(() => ({
        productType: (filters.selectedProductTypes || []).length,
        color: (filters.selectedColors || []).length,
        cannabinoidType: (filters.selectedTypes || []).length > 0 ? 1 : 0,
        price: (filters.price_min || filters.price_max) ? 1 : 0,
        cannabinoidLevel: (filters.cannabinoid_level_min || filters.cannabinoid_level_max) ? 1 : 0,
        terpeneLevel: (filters.terpene_level_min || filters.terpene_level_max) ? 1 : 0,
        specificCannabinoids: (filters.filteredCannabinoids || []).length,
        specificTerpenes: (filters.filteredTerpenes || []).length,
    }), [filters]);

    // ── Render ──

    return (
        <div className={`filter-panel${isOpen ? "" : " filter-panel--collapsed"}`} data-tutorial="showroom-filters">
            <div className="filter-panel__inner">

                {/* Search */}
                <div className="sfp-search-wrap">
                    <svg className="sfp-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" />
                        <line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                        type="text"
                        className="fp-search"
                        placeholder="Search by product name"
                        value={localSearch}
                        onChange={handleSearchChange}
                    />
                </div>

                {/* Active filter tags */}
                {tags.length > 0 && (
                    <div className="sfp-active-tags">
                        <div className="sfp-active-tags__list">
                            {tags.map((tag) => (
                                <span key={tag.key} className="sfp-tag">
                                    {tag.label}
                                    <button
                                        className="sfp-tag__remove"
                                        onClick={() => handleRemoveTag(tag)}
                                        type="button"
                                        aria-label={`Remove ${tag.label}`}
                                    >
                                        ×
                                    </button>
                                </span>
                            ))}
                        </div>
                        <button className="sfp-clear-all" onClick={handleClearAll} type="button">
                            Clear all
                        </button>
                    </div>
                )}

                {/* Accordion sections */}
                <div className="sfp-sections">
                    <AccordionSection
                        title="Product Type"
                        isOpen={openSections.has("productType")}
                        onToggle={() => toggleSection("productType")}
                        activeCount={sectionCounts.productType}
                    >
                        <div className="fp-chips">
                            {productTypeOptions.map((pt) => (
                                <FilterChip
                                    key={pt.value}
                                    label={pt.label}
                                    isSelected={(filters.selectedProductTypes || []).includes(pt.value)}
                                    onClick={() => toggleChip("selectedProductTypes", pt.value)}
                                />
                            ))}
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Color"
                        isOpen={openSections.has("color")}
                        onToggle={() => toggleSection("color")}
                        activeCount={sectionCounts.color}
                    >
                        <div className="fp-chips">
                            {colorOptions.map((c) => (
                                <FilterChip
                                    key={c.value}
                                    label={c.label}
                                    isSelected={(filters.selectedColors || []).includes(c.value)}
                                    onClick={() => toggleChip("selectedColors", c.value)}
                                />
                            ))}
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Cannabinoid Type"
                        isOpen={openSections.has("cannabinoidType")}
                        onToggle={() => toggleSection("cannabinoidType")}
                        activeCount={sectionCounts.cannabinoidType}
                    >
                        <div className="fp-chips">
                            {cannabinoidTypeOptions.map((t) => (
                                <FilterChip
                                    key={t.value}
                                    label={t.label}
                                    isSelected={
                                        (filters.selectedTypes || []).includes(t.value) ||
                                        (filters.selectedTypes || []).includes(`${t.value}v`)
                                    }
                                    onClick={() => toggleType(t.value)}
                                />
                            ))}
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Price"
                        isOpen={openSections.has("price")}
                        onToggle={() => toggleSection("price")}
                        activeCount={sectionCounts.price}
                    >
                        <div className="fp-range-row">
                            <span className="fp-range-label">$</span>
                            <input
                                type="number"
                                className="fp-range-input"
                                placeholder="Min"
                                value={localRanges.price_min}
                                onChange={(e) => handleRangeChange("price_min", e.target.value)}
                            />
                            <span className="fp-range-sep">–</span>
                            <input
                                type="number"
                                className="fp-range-input"
                                placeholder="Max"
                                value={localRanges.price_max}
                                onChange={(e) => handleRangeChange("price_max", e.target.value)}
                            />
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Total Cannabinoids %"
                        isOpen={openSections.has("cannabinoidLevel")}
                        onToggle={() => toggleSection("cannabinoidLevel")}
                        activeCount={sectionCounts.cannabinoidLevel}
                    >
                        <div className="fp-range-row">
                            <input
                                type="number"
                                className="fp-range-input"
                                placeholder="Min"
                                value={localRanges.cannabinoid_level_min}
                                onChange={(e) => handleRangeChange("cannabinoid_level_min", e.target.value)}
                            />
                            <span className="fp-range-sep">–</span>
                            <input
                                type="number"
                                className="fp-range-input"
                                placeholder="Max"
                                value={localRanges.cannabinoid_level_max}
                                onChange={(e) => handleRangeChange("cannabinoid_level_max", e.target.value)}
                            />
                            <span className="fp-range-label">%</span>
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Total Terpenes %"
                        isOpen={openSections.has("terpeneLevel")}
                        onToggle={() => toggleSection("terpeneLevel")}
                        activeCount={sectionCounts.terpeneLevel}
                    >
                        <div className="fp-range-row">
                            <input
                                type="number"
                                className="fp-range-input"
                                placeholder="Min"
                                value={localRanges.terpene_level_min}
                                onChange={(e) => handleRangeChange("terpene_level_min", e.target.value)}
                            />
                            <span className="fp-range-sep">–</span>
                            <input
                                type="number"
                                className="fp-range-input"
                                placeholder="Max"
                                value={localRanges.terpene_level_max}
                                onChange={(e) => handleRangeChange("terpene_level_max", e.target.value)}
                            />
                            <span className="fp-range-label">%</span>
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Specific Cannabinoids"
                        isOpen={openSections.has("specificCannabinoids")}
                        onToggle={() => toggleSection("specificCannabinoids")}
                        activeCount={sectionCounts.specificCannabinoids}
                    >
                        <div className="fp-chips">
                            {cannabinoidsOptions.map((c) => (
                                <FilterChip
                                    key={c.value}
                                    label={c.label}
                                    isSelected={(filters.filteredCannabinoids || []).includes(c.value)}
                                    onClick={() => toggleChip("filteredCannabinoids", c.value)}
                                />
                            ))}
                        </div>
                    </AccordionSection>

                    <AccordionSection
                        title="Specific Terpenes"
                        isOpen={openSections.has("specificTerpenes")}
                        onToggle={() => toggleSection("specificTerpenes")}
                        activeCount={sectionCounts.specificTerpenes}
                    >
                        <div className="fp-chips">
                            {terpeneOptions.map((t) => (
                                <FilterChip
                                    key={t.value}
                                    label={t.label}
                                    isSelected={(filters.filteredTerpenes || []).includes(t.value)}
                                    onClick={() => toggleChip("filteredTerpenes", t.value)}
                                />
                            ))}
                        </div>
                    </AccordionSection>
                </div>

            </div>
        </div>
    );
};

export default FilterPanel;
